/**
 * Redondea un número a N decimales sin imprecisiones de punto flotante.
 * Usa redondeo bancario (round half to even) para minimizar sesgo acumulado.
 */
export function roundTo(value, decimals = 2) {
    const factor = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * factor) / factor;
}
/**
 * Crea un MoneyAmount convirtiendo un monto original a la moneda base.
 */
export function createMoneyAmount(monto, moneda, tasaCambio, monedaBase = 'COP') {
    const montoBase = moneda === monedaBase ? monto : monto * tasaCambio;
    return {
        monto: roundTo(monto, 4),
        moneda,
        tasaCambio,
        montoBase: roundTo(montoBase, 4),
    };
}
const SYMBOLS = {
    COP: '$',
    USD: 'US$',
    VES: 'Bs.',
    EUR: '€',
    USDT: 'USDT',
};
function symbolFor(moneda) {
    return SYMBOLS[moneda] ?? `${moneda} `;
}
/**
 * Locale por defecto para todo el sistema.
 * es-CO: punto (.) para miles, coma (,) para decimales.
 */
export const DEFAULT_LOCALE = 'es-CO';
const moneyFormatters = new Map();
function getMoneyFormatter(decimals, locale = DEFAULT_LOCALE) {
    const key = `${locale}:${decimals}`;
    let fmt = moneyFormatters.get(key);
    if (!fmt) {
        fmt = new Intl.NumberFormat(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
        moneyFormatters.set(key, fmt);
    }
    return fmt;
}
/**
 * Formatea un monto para display con símbolo de moneda.
 * Convención es-CO: $1.234,56 · US$1,234.56 se mantiene en formato origen para monedas extranjeras
 * cuando `decimals` es 2; pero para COP/VES siempre usamos es-CO.
 *
 * Ejemplos:
 *   formatMoney(1234.5)              → "$1.234,50"
 *   formatMoney(1234.5, 'USD')       → "US$1.234,50"
 *   formatMoney(1234.5, 'COP', 0)    → "$1.234"
 */
export function formatMoney(monto, moneda = 'COP', decimals = 2, locale = DEFAULT_LOCALE) {
    return `${symbolFor(moneda)}${getMoneyFormatter(decimals, locale).format(monto)}`;
}
/**
 * Formatea un número SIN símbolo — para KPIs que ya tienen prefijo "$" en el layout,
 * o para unidades (10.244,75).
 *
 * Ejemplos:
 *   formatNumber(1234567.89)         → "1.234.567,89"
 *   formatNumber(1234567.89, 0)      → "1.234.567"
 *   formatNumber(1234, 4)            → "1.234,0000"
 */
export function formatNumber(monto, decimals = 2, locale = DEFAULT_LOCALE) {
    return getMoneyFormatter(decimals, locale).format(monto);
}
/**
 * Formatea un porcentaje: 0.1234 → "12,34%".
 *
 * El argumento `value` es la FRACCIÓN (0-1); la función multiplica por 100
 * antes de formatear. Para un valor que ya está en escala de porcentaje
 * (p. ej. 12.34), dividir primero por 100 o usar `formatNumber(12.34, 2) + '%'`.
 */
export function formatPercent(value, decimals = 2, locale = DEFAULT_LOCALE) {
    return `${getMoneyFormatter(decimals, locale).format(value * 100)}%`;
}
/**
 * Sanitiza texto de un input de dinero a forma canónica con punto decimal
 * (apto para `Number()`), respetando la convención es-CO: la coma es el
 * separador decimal preferido y el punto de miles se ignora.
 *
 * Ejemplos:
 *   sanitizeMoneyInput("1.234,56")  → "1234.56"
 *   sanitizeMoneyInput("1234,5")    → "1234.5"
 *   sanitizeMoneyInput("12.34")     → "12.34"   (punto como decimal si no hay coma)
 *   sanitizeMoneyInput("12,3456", 2)→ "12.34"
 *   sanitizeMoneyInput("abc")       → ""
 */
export function sanitizeMoneyInput(raw, maxDecimals = 2) {
    if (raw == null)
        return '';
    const cleaned = String(raw).replace(/[^\d.,]/g, '');
    if (!cleaned)
        return '';
    const lastComma = cleaned.lastIndexOf(',');
    const lastDot = cleaned.lastIndexOf('.');
    let decimalSep = null;
    if (lastComma >= 0 && lastDot >= 0) {
        // El último separador gana (el otro se trata como miles).
        decimalSep = lastComma > lastDot ? ',' : '.';
    }
    else if (lastComma >= 0) {
        decimalSep = ',';
    }
    else if (lastDot >= 0) {
        decimalSep = '.';
    }
    if (!decimalSep) {
        return cleaned.replace(/[.,]/g, '');
    }
    const idx = cleaned.lastIndexOf(decimalSep);
    const intPart = cleaned.slice(0, idx).replace(/[.,]/g, '') || '0';
    const decPart = cleaned
        .slice(idx + 1)
        .replace(/[.,]/g, '')
        .slice(0, Math.max(0, maxDecimals));
    // Conserva el separador pendiente mientras el usuario escribe "12,".
    if (decPart.length === 0 && cleaned.endsWith(decimalSep)) {
        return `${intPart}.`;
    }
    return decPart.length > 0 ? `${intPart}.${decPart}` : intPart;
}
/**
 * Parsea un valor de input de dinero (canónico o con coma) a number.
 * Devuelve NaN si está vacío o no es finito.
 */
export function parseMoneyInput(raw) {
    if (raw == null || raw === '')
        return NaN;
    if (typeof raw === 'number')
        return Number.isFinite(raw) ? raw : NaN;
    const canonical = sanitizeMoneyInput(String(raw), 8);
    if (!canonical || canonical === '.')
        return NaN;
    const n = Number(canonical);
    return Number.isFinite(n) ? n : NaN;
}
/**
 * Sanitiza un input de cantidad entera: solo dígitos, sin signos ni decimales.
 *   sanitizeIntegerInput("1.004") → "1004"
 *   sanitizeIntegerInput("12a3")  → "123"
 */
export function sanitizeIntegerInput(raw) {
    if (raw == null)
        return '';
    return String(raw).replace(/\D/g, '');
}
/**
 * Parsea cantidad entera. Devuelve NaN si vacío o no es entero ≥ 0.
 */
export function parseIntegerInput(raw) {
    if (raw == null || raw === '')
        return NaN;
    if (typeof raw === 'number') {
        return Number.isInteger(raw) && raw >= 0 ? raw : NaN;
    }
    const digits = sanitizeIntegerInput(String(raw));
    if (!digits)
        return NaN;
    const n = Number(digits);
    return Number.isInteger(n) && n >= 0 ? n : NaN;
}
// =========================================================================
// Fechas — sprint 99 I.5 (unificación Intl.DateTimeFormat)
// =========================================================================
const dateFormatters = new Map();
function getDateFormatter(style, locale = DEFAULT_LOCALE) {
    const key = `${locale}:${typeof style === 'string' ? style : JSON.stringify(style)}`;
    let fmt = dateFormatters.get(key);
    if (!fmt) {
        if (typeof style === 'string') {
            // medium para fechas (año 4 dígitos), short para hora. Default browser-like.
            fmt =
                style === 'date'
                    ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
                    : style === 'time'
                        ? new Intl.DateTimeFormat(locale, { timeStyle: 'short' })
                        : new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' });
        }
        else {
            fmt = new Intl.DateTimeFormat(locale, style);
        }
        dateFormatters.set(key, fmt);
    }
    return fmt;
}
/**
 * Formatea una fecha como "dd/mm/yyyy" (es-CO) o "mm/dd/yyyy" (en-US).
 * Acepta Date o string ISO; valores invalidos devuelven "".
 */
export function formatDate(value, locale = DEFAULT_LOCALE) {
    if (!value)
        return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return '';
    return getDateFormatter('date', locale).format(d);
}
/**
 * Formatea fecha+hora "dd/mm/yyyy hh:mm" (es-CO).
 */
export function formatDateTime(value, locale = DEFAULT_LOCALE) {
    if (!value)
        return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return '';
    return getDateFormatter('datetime', locale).format(d);
}
/**
 * Solo hora "hh:mm".
 */
export function formatTime(value, locale = DEFAULT_LOCALE) {
    if (!value)
        return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return '';
    return getDateFormatter('time', locale).format(d);
}
/**
 * Variantes con Intl.DateTimeFormatOptions custom. Útil cuando se necesita
 * `dateStyle: 'long'` o similares que las 3 variantes shorthand no cubren.
 */
export function formatDateCustom(value, options, locale = DEFAULT_LOCALE) {
    if (!value)
        return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime()))
        return '';
    return getDateFormatter(options, locale).format(d);
}
//# sourceMappingURL=money.js.map